import { Inject, Injectable } from '@angular/core'
import { Section } from '@flocasts/flosports30-types/dist/watch'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { catchError, map } from 'rxjs/operators'
import { Observable, of } from 'rxjs'
import { LOGGER_SERVICE } from '../../logger/logger.config'
import { LoggerService } from '../../logger/logger.interface'
import { AuthenticatedStreamResponse } from '@flocasts/flosports30-types/dist/live-api'
import { FloResponse } from '@flocasts/flosports30-types/dist/flo-rest-api/response'
import { EnvironmentService } from '../../singleton-services/environment.service'
import { CacheDataService } from 'src/app/singleton-services/cache-data.service'
import { EXPERIENCE_API_WATCH_BASE_PATH } from 'src/app/app.config'

// Watch does not use experience service types and needs to remain on an older version
export const WATCH_API_VERSION = '1.7.82'
export const WATCH_API_DEFAULT_LIMIT = 4

interface WatchResponse {
  sections: ReadonlyArray<Section>
}

@Injectable()
export class WatchDataService {
  constructor(
    private readonly http: HttpClient,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService,
    private readonly es: EnvironmentService,
    private readonly cacheDataService: CacheDataService
  ) {}

  public getWatchSections = (
    siteId: number,
    offset: number,
    limit = WATCH_API_DEFAULT_LIMIT
  ): Observable<ReadonlyArray<Section>> => {
    const params = new HttpParams({
      fromObject: {
        version: WATCH_API_VERSION,
        limit: limit.toString(),
        offset: offset.toString(),
        site_id: siteId.toString(),
        filterSiteIds: siteId.toString()
      }
    })
    return this.cacheDataService.wrapper<WatchResponse>(EXPERIENCE_API_WATCH_BASE_PATH, params, true).pipe(
      map(res => res?.sections || []),
      catchError((err: HttpErrorResponse) => {
        this.logger.error('WatchDataService GetWatchSections Error: ', err)
        return of([])
      })
    )
  }

  /**
   * When using this service, ensure that only urls with experiences are being passed in.
   * In the component we are testing to ensure the url actually includes experiences before calling.
   */
  public getWatchSectionsByUrl = (url: string): Observable<ReadonlyArray<Section>> => {
    return this.http.get<WatchResponse>(url).pipe(
      map(res => res.sections),
      catchError((err: HttpErrorResponse) => {
        this.logger.error('WatchDataService GetWatchSectionsByUrl Error: ', err)
        return of([])
      })
    )
  }

  /**
   * Get a live preview stream for a given streamId.
   */
  public getLivePreviewStreamToken = (streamId: number): Observable<AuthenticatedStreamResponse> => {
    return this.http
      .post<FloResponse<AuthenticatedStreamResponse, {}>>(
        `${this.es.config.endpoints.live}/streams/${streamId}/tokens/preview`,
        {
          adTracking: {
            appName: 'flosports-web'
          }
        }
      )
      .pipe(
        map(res => res.data),
        catchError((err: HttpErrorResponse) => {
          this.logger.error('WatchDataService getLivePreviewStreamToken Error: ', err)
          return of({} as AuthenticatedStreamResponse)
        })
      )
  }
}
